import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Store } from "@ngrx/store";
import { NavController } from "ionic-angular";
import { has, isString } from "lodash";
import { Subscription } from "rxjs";
import { Observable } from "rxjs/Observable";
import { PurchasesData } from "../../providers/purchases";
import { Utilities } from "../../providers/utilities";
import * as sharedSelectors from "../../shared/reducers/shared.reducer";
import { take } from "rxjs/operator/take";

@Component({
  selector: "purchases-list",
  templateUrl: "purchases-list.html",
})
export class PurchasesListComponent implements OnInit, OnDestroy {

  @Input() public isPage: boolean = false;
  @Output() public emitRecords: EventEmitter<any> = new EventEmitter<any>();

  public purchases$: Observable<{}>;
  public purchasesTmp$: Observable<{}>;
  public openDetail: number;
  public clientId: string;
  public limitTo: number = 10;
  private clientSubscribe: Subscription;

  constructor(
    public navCtrl: NavController,
    private store: Store<{}>,
    private purchaseData: PurchasesData,
    private utilities: Utilities,
  ) { }

  public ngOnInit() {
    this.clientSubscribe = this.store.select(sharedSelectors.getClientId).subscribe((clientId) => {
      if (clientId) {
        this.clientId = clientId;
        this.purchasesTmp$ = this.purchaseData.getAllByClient(clientId)
          .map((list) => list.reduce((total, next) => {
            return { ...total, ...next };
          }, {}))
          .map((list) => Object.keys(list).map((i) => list[i]))
          .map((list) => list.filter((value) => has(value, "summary")))
          .map((list) => list.filter((value) => has(value, "userId")))
          .map((list) => list.map((item) => {
            if (!has(item, "transaction")) {
              const distance = this.utilities.timeDifference(item.summary.created_at);
              this.utilities.consoleDecorator("Distancia del item", {
                distance, status: this.calculateDistance(distance) ? "ABANDONED SHOPPING CART" : "PROCESS PAYMENT",
              });
              return {
                ...item, transaction: {
                  ...item.summary,
                  amount: item.summary.total,
                  emulated: true,
                  id: "NA",
                  payment_date: item.summary.created_at,
                  status: this.calculateDistance(distance) ? "ABANDONED SHOPPING CART" : "PROCESS PAYMENT",
                },
              };
            }
            return item;
          }))
          .map((list) => list.filter((value) => has(value, "transaction")))
          .map((list) => list.sort((a, b) => {
            a = new Date(a.transaction.payment_date);
            b = new Date(b.transaction.payment_date);
            return a > b ? -1 : a < b ? 1 : 0;
          }))
          .do((list) => this.emitRecords.emit(list));

        this.purchases$ = this.purchasesTmp$;
      }
    });
  }

  public isValid(item) {
    return item.transaction.status === "success" ||
      item.transaction.status === "CAPTURED" ||
      item.transaction.status === "APPROVED" ||
      item.transaction.status === "PROCESS PAYMENT";
  }

  public search(ev) {
    this.purchases$ = this.purchasesTmp$.map((items: any) => items.filter((item) => this.utilities.searchItem(
      ev.target.value,
      item,
      ["id", { transaction: ["id", "payment_date", "authorization_code", "amount", "status"] }],
    )));
  }

  public viewFormInfo(itemId: string, formData: any[], formFields: any[], type: string, shopId: string = null) {
    this.utilities.Modal.create("PurchasesFormPage", {
      formData,
      formFields,
      itemId,
      shopId,
      type,
    }).present();
  }

  public delivered(userId, itemId, payId, type) {
    return this.purchaseData.updateDeliveredItem(userId, payId, itemId, this.clientId, type)
      .then(() => this.utilities.setToast("The delivery status of the item has been changed."));
  }

  public handlerTypeProductToSendNotification(payment, email, type) {
    if (type === "ticket") {
      this.selectNotificationTypeTicket(payment, email);
    } else {
      this.goSendPaymentGeneral(payment, email);
    }
  }

  public selectNotificationTypeTicket(payment, email) {
    this.utilities.Alert.create({
      buttons: [{
        role: "cancel",
        text: "Cancel",
      }, {
        handler: (data) => {
          if (data === "sendPayment") {
            this.goSendPaymentGeneral(payment, email);
          } else {
            this.goSendQREmailTicket(payment);
          }
        },
        text: "Ok",
      }],
      inputs: [{
        label: "Send payment receipt",
        name: "sendPayment",
        type: "radio",
        value: "sendPayment",
      },
      {
        label: "Send tickets QR",
        name: "sendQR",
        type: "radio",
        value: "sendQR",
      },
      ],
      message: "Select option for send email notification",
      title: "Select Option",
    }).present();
  }

  public goSendPaymentGeneral(payment, email) {
    this.utilities.Alert.create({
      buttons: [{
        role: "cancel",
        text: "Cancel",
      }, {
        handler: (data) => {
          if (data.to && data.to !== "") {
            const load = this.utilities.setLoading();
            this.purchaseData.sendPaymentNotification(
              this.clientId,
              payment.userId,
              payment.id,
              [data.to],
            ).subscribe((result) => {
              load.dismiss()
                .then(() => this.utilities.setToast("The notification has been sent correctly!"))
                .then(() => this.utilities.consoleDecorator("Result email send", [result, payment]));
            });

          } else {
            return false;
          }
        },
        text: "Send",
      }],
      inputs: [{
        name: "to",
        type: "email",
        value: email,
      }],
      message: "Enter the email address for send notification of the payment",
      title: "Send notification",
    }).present();
  }

  public goSendQREmailTicket(payment) {
    this.utilities.Alert.create({
      buttons: [{
        role: "cancel",
        text: "Cancel",
      }, {
        handler: () => {
          const load = this.utilities.setLoading();
          this.purchaseData.sendPaymentNotificationQR(
            this.clientId,
            payment.userId,
            payment.id,
          ).subscribe((result) => {
            load.dismiss()
              .then(() => this.utilities.setToast("The QR code has been sent correctly!"))
              .then(() => this.utilities.consoleDecorator("Result email send", [result, payment]));
          });
        },
        text: "Send",
      }],
      message: "Are you sure to send an email with the QR of the ticket to the emails registered in the purchase?",
      title: "Send QR Email",
    }).present();
  }


  public loadMore(infiniteScroll, list: any[]) {
    if (this.limitTo < list.length) {
      this.limitTo += 10;
      setTimeout(() => {
        infiniteScroll.complete();
      }, 300);
    } else {
      infiniteScroll.enable(false);
    }
  }

  public viewUser(item: any = null) {
    if (item) {
      const object = {
        Company: { value: item.company },
        Email: { value: item.email },
        ID: { value: item.id, actions: { copy: true } },
        Name: { value: item.displayName },
        Phone: { value: item.phone },
        Position: { value: item.position },
      };
      this.utilities.Modal.create("ModalViewPage", { object, title: "User detail" }).present();
    }
  }

  public isOpen(key) {
    return this.openDetail === key;
  }

  public calculateName(transactionData) {
    switch (transactionData.type) {
      case "ticket":
        return this.calculateAmount(transactionData, "tickets") ?
          `Sale ${this.calculateAmount(transactionData, "tickets")} ticket(s)` :
          "Sale tickets";
      case "catalog":
        return this.calculateAmount(transactionData, "products") ?
          `Sale ${this.calculateAmount(transactionData, "products")} product(s)` :
          "Sale products";
      case "reserve":
        return this.calculateAmount(transactionData, "reserves") ?
          `Sale ${this.calculateAmount(transactionData, "reserves")} reserve(s)` :
          "Sale reserve";
      default:
        return "Sale";
    }
  }

  public open(key) {
    if (this.openDetail === key) {
      this.openDetail = null;
    } else {
      this.openDetail = key;
    }
  }

  public isString(data: any) {
    return isString(data);
  }

  public is(item, param) {
    return has(item, param);
  }

  public ngOnDestroy() {
    if (this.clientSubscribe) { this.clientSubscribe.unsubscribe(); }
  }

  private calculateDistance(distance) {
    // tslint:disable-next-line:use-isnan
    if (distance.day === NaN || distance.hour === NaN) {
      return true;
    }

    if (distance.day === 0) {
      return false;
    }

    if (distance.day === 1 && distance.hour <= 23) {
      return false;
    }

    return true;
  }

  private calculateAmount(transactionData: any, type: string) {
    if (!this.validaReserveContent(transactionData, type)) { return false; }
    let total = 0;
    Object.keys(transactionData.summary[type]).map((key) => {
      total += transactionData.summary[type][key].sale_amount;
    });
    return total;
  }
  private validaReserveContent(transactionData, param: string) {
    if (has(transactionData, "summary")) {
      if (has(transactionData.summary, param)) {
        return true;
      }
    }
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/components/purchases-list/purchases-list.ts